import type { RawMeasurementResult } from './types';

/** Internal metric model. A field is present only when it was measured with enough samples to be meaningful. */
export interface NormalizedMetrics {
  downloadMbps?: number;
  uploadMbps?: number;
  /** Idle (unloaded) latency, median of the engine's samples. */
  latencyMs?: number;
  jitterMs?: number;
  /** Latency while downloading. */
  loadedLatencyMs?: number;
  /** loadedLatencyMs − latencyMs, floored at 0. */
  latencyIncreaseMs?: number;
}

/** Fewer samples than this and the value is not shown or scored. */
export const MIN_SAMPLES = { idleLatency: 5, loadedLatency: 3 } as const;

const usable = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

export function normalize(raw: RawMeasurementResult): NormalizedMetrics {
  const { engine, upload } = raw;
  const metrics: NormalizedMetrics = {};
  if (usable(engine.downloadMbps) && engine.downloadMbps > 0) metrics.downloadMbps = engine.downloadMbps;
  if (usable(upload.mbps) && upload.mbps > 0 && upload.errors.length === 0) metrics.uploadMbps = upload.mbps;
  if (usable(engine.idleLatencyMs) && engine.idleLatencySamples >= MIN_SAMPLES.idleLatency) {
    metrics.latencyMs = engine.idleLatencyMs;
    if (usable(engine.idleJitterMs)) metrics.jitterMs = engine.idleJitterMs;
  }
  if (
    metrics.latencyMs !== undefined &&
    usable(engine.loadedLatencyMs) &&
    engine.loadedLatencySamples >= MIN_SAMPLES.loadedLatency
  ) {
    metrics.loadedLatencyMs = engine.loadedLatencyMs;
    metrics.latencyIncreaseMs = Math.max(0, engine.loadedLatencyMs - metrics.latencyMs);
  }
  return metrics;
}

/** Download, upload and idle latency are required for a result; without them no score is shown. */
export function missingCoreMetrics(metrics: NormalizedMetrics): string[] {
  const missing: string[] = [];
  if (metrics.downloadMbps === undefined) missing.push('download');
  if (metrics.uploadMbps === undefined) missing.push('upload');
  if (metrics.latencyMs === undefined) missing.push('latency');
  return missing;
}
